import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Clock, ExternalLink, Gamepad2, Gift, Headset, Landmark, MessageCircle, MessageSquareText, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/components/account/PageHeader.js';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { openChatwootChat } from '@/lib/chatwoot/index.js';
import { useBrandingStore } from '@/stores/branding.js';

const LINE_GREEN = '#06C755';

// Public like /casino — guests may need help with login/register too,
// so no requireAuth here. The widget identifies them once they sign in.
export const Route = createFileRoute('/_player/support')({
  component: SupportScreen,
});

interface Topic {
  key: 'deposit' | 'account' | 'games' | 'promo';
  icon: typeof Landmark;
}

const TOPICS: Topic[] = [
  { key: 'deposit', icon: Landmark },
  { key: 'account', icon: ShieldCheck },
  { key: 'games', icon: Gamepad2 },
  { key: 'promo', icon: Gift },
];

function SupportScreen() {
  const { t } = useTranslation('support');
  const lineId = useBrandingStore((s) => s.branding?.support?.lineId) ?? null;
  const lineAddUrl = lineId ? `https://line.me/R/ti/p/${encodeURIComponent(lineId)}` : null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <Card featured className="relative overflow-hidden p-6 md:p-8">
        <div
          className="pointer-events-none absolute -right-16 -top-16 size-56 rounded-full bg-[var(--accent)]/15 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-col items-start gap-5 md:flex-row md:items-center">
          <div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent)]/15 text-[var(--accent-strong)]">
            <Headset size={28} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-extrabold text-[var(--text-primary)]">
              {t('chat.title')}
            </h2>
            <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-[var(--text-secondary)]">
              {t('chat.description')}
            </p>
            <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--text-muted)]">
              <Clock size={13} />
              {t('chat.note')}
            </p>
          </div>
          <Button size="lg" onClick={() => openChatwootChat()} className="shrink-0">
            <MessageCircle size={18} />
            {t('chat.cta')}
          </Button>
        </div>
      </Card>

      {lineId && (
        <Card className="p-5 md:p-6">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
            <div
              className="flex size-12 shrink-0 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${LINE_GREEN}26`, color: LINE_GREEN }}
            >
              <MessageSquareText size={24} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-[15px] font-extrabold text-[var(--text-primary)]">
                  {t('line.title')}
                </h2>
                <span className="rounded-md bg-[var(--bg-elevated)] px-2 py-0.5 font-mono text-[12px] font-bold text-[var(--text-secondary)]">
                  {lineId}
                </span>
              </div>
              <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-[var(--text-secondary)]">
                {t('line.description')}
              </p>
            </div>
            <a
              href={lineAddUrl ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 shrink-0 items-center gap-2 rounded-xl px-5 text-[14px] font-bold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: LINE_GREEN }}
            >
              {t('line.cta')}
              <ExternalLink size={15} />
            </a>
          </div>
        </Card>
      )}

      <div>
        <h3 className="text-[15px] font-extrabold text-[var(--text-primary)]">
          {t('topics.title')}
        </h3>
        <p className="mt-0.5 text-[12px] text-[var(--text-muted)]">{t('topics.hint')}</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {TOPICS.map(({ key, icon: TopicIcon }) => (
          <Card
            key={key}
            as="button"
            hoverable
            onClick={() => openChatwootChat()}
            className="flex items-start gap-3.5 p-4 text-left"
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--bg-elevated)] text-[var(--accent-strong)]">
              <TopicIcon size={19} />
            </div>
            <div className="min-w-0">
              <div className="text-[14px] font-bold text-[var(--text-primary)]">
                {t(`topics.${key}.title`)}
              </div>
              <div className="mt-0.5 text-[12px] leading-relaxed text-[var(--text-secondary)]">
                {t(`topics.${key}.desc`)}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
